import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import {
  Box,
  ButtonPrimary1,
  ButtonSecondary,
  getCompanyName,
  HStack,
  VStack,
} from "../../components/utils";
import { CorporateActionDetails } from "../../types/CompanyProfile";
import { formatDisplayDate } from "../../utils/date";
import { downloadBlobObject } from "../../utils/DownloadFile";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import StatusLabel from "../esopOverview/StatusLabel";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import {
  useDiscardBonusRatioChange,
  useDiscardCorporateAction,
} from "../../queries/onBoarding";
import Tooltip from "../../components/shared/Tooltip";
import { usePermissionStore } from "../../store";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Resource,
  Action as DefaultAction,
} from "../../utils/interfaces/Companies";

function CorporateActionDetailsCard({
  corporateAction,
  openBonusDialog,
  openSplitDialog,
  openBonusRatioChangeDialog,
}: {
  corporateAction: CorporateActionDetails;
  openBonusDialog: Function;
  openSplitDialog: Function;
  openBonusRatioChangeDialog: Function;
}) {
  const currency = getCurrencyType();

  const { permission } = usePermissionStore();

  const isAdmin = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.All,
    DefaultAction.Edit
  );

  const { mutate: discardCorporateAction } = useDiscardCorporateAction();
  const { mutate: discardBonusRatioChange } = useDiscardBonusRatioChange();

  const handleAction = (action: Action) => {
    if (action.name === "Discard") {
      if (corporateAction.type === "BONUS_RATIO_CHANGE") {
        discardBonusRatioChange(corporateAction.id || "", {
          onSuccess: () => {
            toast("Record Discarded Successfully", {
              type: "success",
              autoClose: 2000,
            });
          },
          onError: () => {
            toast("Error Discarding Record", {
              type: "error",
              autoClose: 2000,
            });
          },
        });
      } else {
        discardCorporateAction(corporateAction.id || "", {
          onSuccess: (data) => {
            toast("Record Discarded Successfully", {
              type: "success",
              autoClose: 2000,
            });
          },
          onError: (error) => {
            toast("Error Discarding Record", {
              type: "error",
              autoClose: 2000,
            });
          },
        });
      }
    }
  };

  return (
    <Box className="w-full bg-white shadow-md rounded-md">
      <Box className="px-10 py-4 border-b">
        <HStack className="justify-between">
          <HStack className="gap-3 text-lg font-medium text-left items-center">
            <h5>{corporateAction.name}</h5>
            <StatusLabel state={corporateAction.state || ""} />
          </HStack>
          <CTADropdown
            dropdownClassName=""
            actions={[
              {
                name: "Discard",
                disabled: corporateAction.state === "APPROVED",
              },
            ]}
            onAction={(action) => handleAction(action)}
          />
        </HStack>
        <HStack className="py-4 pt-3 space-between items-center">
          <VStack className="w-full pr-5">
            <HStack className="items-center">
              <h5 className="text-lg font-medium text-left">
                {corporateAction.type === "BONUS_RATIO_CHANGE"
                  ? corporateAction.metaData?.bonusRatioChangeDetails
                      ?.ratioMode === "REPLACE"
                    ? `${corporateAction.ratio}`
                    : `1:${corporateAction.ratio}`
                  : `1:${corporateAction.ratio}`}
              </h5>
              <p className="text-xs font-semi-medium text-gray-light px-3 whitespace-nowrap">
                {`(${corporateAction.ratio} ${
                  corporateAction.type === "BONUS"
                    ? "bonus option(s) for every 1 option"
                    : corporateAction.type === "BONUS_RATIO_CHANGE"
                    ? corporateAction.metaData?.bonusRatioChangeDetails
                        ?.ratioMode === "REPLACE"
                      ? "ratio override"
                      : "bonus option(s) for every 1 option"
                    : "split share(s) for every 1 Equivalent share"
                })`}
              </p>
            </HStack>
            <p className="text-sm font-semi-medium text-gray-light">
              {corporateAction.type === "BONUS"
                ? "Bonus Ratio"
                : corporateAction.type === "BONUS_RATIO_CHANGE"
                ? "Bonus Ratio Change"
                : "Split Ratio"}
            </p>
          </VStack>
          <VStack className="w-full">
            <h5 className="text-lg font-medium text-left">
              {formatDisplayDate(corporateAction.date)}
            </h5>
            <p className="text-sm font-semi-medium text-gray-light">
              {corporateAction.type === "BONUS"
                ? "Bonus Date"
                : corporateAction.type === "BONUS_RATIO_CHANGE"
                ? "Effective Date"
                : "Split Date"}
            </p>
          </VStack>
          {corporateAction.bonusStrikePrice && (
            <VStack className="w-full">
              <h5 className="text-lg font-medium text-left">
                {formatCurrency(corporateAction.bonusStrikePrice, currency)}
              </h5>
              <p className="text-sm font-semi-medium text-gray-light">
                Bonus Strike Price
              </p>
            </VStack>
          )}
          {corporateAction.s3url &&
            (corporateAction.type === "BONUS" ||
              corporateAction.type === "BONUS_RATIO_CHANGE") && (
              <VStack className="w-full">
                <BonusSummaryReport s3url={corporateAction?.s3url || ""} />
                <p className="text-sm font-semi-medium text-gray-light pl-7">
                  Bonus Summary Report
                </p>
              </VStack>
            )}

          {corporateAction.s3url && corporateAction.type === "SPLIT" && (
            <VStack className="w-full">
              <BonusSummaryReport
                s3url={corporateAction?.s3url || ""}
                type="SPLIT"
              />
              <p className="text-sm font-semi-medium text-gray-light pl-7">
                Split Summary Report
              </p>
            </VStack>
          )}
          <VStack className="pl-10">
            <ButtonPrimary1
              disabled={!isAdmin}
              onClick={() => {
                if (!isAdmin) return;
                if (corporateAction.type === "SPLIT") {
                  openSplitDialog(corporateAction);
                  return;
                }
                if (corporateAction.type === "BONUS_RATIO_CHANGE") {
                  openBonusRatioChangeDialog(corporateAction);
                  return;
                }
                openBonusDialog(corporateAction);
              }}
            >
              Details
            </ButtonPrimary1>
          </VStack>
        </HStack>
      </Box>
    </Box>
  );
}

export function BonusSummaryReport({
  s3url,
  type = "BONUS",
}: {
  s3url: string;
  type?: string;
}) {
  const bonusReportName = `${
    type === "BONUS" ? "Bonus" : "Split"
  }_Report_${getCompanyName()}.xlsx`;
  const downloadFile = async () => {
    const blob = await (await fetch(s3url)).blob();
    downloadBlobObject(blob, bonusReportName);
  };
  return (
    <Tooltip text={"Click to download Report"}>
      <div
        className="cursor-pointer"
        onClick={() => {
          downloadFile();
        }}
      >
        <HStack className="items-center">
          <Icon icon="mdi:file-excel" width={32} color="#51A687" />
          <p className="text-sm font-semi-medium hover:text-red-600 underline">
            {type === "BONUS" ? "Bonus_Summary.xlsx" : "Split_Summary.xlsx"}
          </p>
        </HStack>
      </div>
    </Tooltip>
  );
}

export default CorporateActionDetailsCard;
